/**
 * Who a conversation is with: the name to print, and the address to group on.
 *
 * Two answers to the same question, kept together because they must never
 * disagree. The name is what a row says; the address is what decides that two
 * rows are the same correspondent. Splitting them into the component that draws
 * one and the component that draws the other is how a folded line ends up
 * headed by somebody who is not in it.
 *
 * Pure on purpose: no React and no component import, so both rules can be
 * tested without a browser. `ThreadRowItems` re-exports `threadWho`, so every
 * caller that already imported it from there keeps working.
 */
import type { MailThread } from "../../../lib/mail";

/**
 * The best name available, in order: the matched company, then the linked
 * client, then the sender's own name, then the bare address.
 *
 * An operator triaging a morning's mail is looking for a company, and falling
 * straight through to `noreply@` would hide the one thing they scan for.
 */
export function threadWho(th: MailThread): string {
  const last = th.messages?.[0];
  return th.matchedCompanyName ?? th.client?.name ?? last?.fromName ?? last?.fromAddress ?? "-";
}

/**
 * THEIR address on this conversation, lowercased. "" when the row carries none.
 *
 * Not a fixed column. On an outbound message `fromAddress` is our own mailbox,
 * so reading that column on a thread we started would answer "who is this with"
 * with our own name - and in the Sent folder it would answer it the same way
 * for every row on the screen. The mail twin of `callCounterpart`, and it goes
 * wrong in exactly the same way when somebody reaches for a column instead.
 *
 * Lowercased because a mail address is case-insensitive in the part that
 * matters, and two spellings of one person is the bug this whole rule exists to
 * avoid. Only the newest message is read - it is the only one the list row
 * carries - so a thread that changed hands is grouped by whoever is on it now.
 */
export function threadAddress(th: MailThread): string {
  const last = th.messages?.[0];
  if (!last) return "";
  const raw = last.direction === "inbound" ? last.fromAddress : (last.toAddresses?.[0] ?? "");
  return (raw ?? "").trim().toLowerCase();
}
